/**
 * The one place that talks to the server.
 *
 * Two things are deliberate here:
 *
 * 1. Auth is a bearer token, never a cookie. The Android WebView serves the app
 *    from https://localhost, so every API call is cross-origin — a cookie
 *    session would need SameSite=None and Allow-Credentials, which some WebView
 *    builds drop, and it would drag CSRF back in. One token path serves both
 *    platforms.
 *
 * 2. API_BASE is empty in the web build (same-origin, so nginx handles it) and
 *    an absolute URL in the APK build, injected at build time by Vite. Nothing
 *    else in the app knows which platform it's on.
 */

import { persistToken } from '../native/storage'
import type { DistancePref } from '../lib/units'

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')

const TOKEN_KEY = 'lift.token'

export class ApiError extends Error {
  status: number
  code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

/**
 * Token storage.
 *
 * localStorage on web. In the APK this is swapped for Capacitor Preferences
 * (which survives a WebView data clear) behind the same three functions — see
 * src/native/. Keeping the interface this small is what makes that swap a
 * one-file change.
 */
export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY)
    else localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* private mode / storage disabled — the session just won't persist */
  }
  // Mirror into native storage, which survives a WebView data clear. No-op on
  // web. Fire-and-forget so the synchronous contract of setToken is preserved —
  // see native/storage.ts for why that matters.
  void persistToken(token)
}

type RequestOpts = {
  method?: string
  body?: unknown
  signal?: AbortSignal
}

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}/api/v1${path}`, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    // Explicitly omit — the API takes no cookies, and sending them would
    // require Allow-Credentials on the server side for no benefit.
    credentials: 'omit',
  })

  if (res.status === 204) return undefined as T

  let payload: any = null
  try {
    payload = await res.json()
  } catch {
    /* non-JSON body (a proxy error page, say) — fall through to the raw status */
  }

  if (!res.ok) {
    // 401 means the token is gone or revoked. Clear it so the UI drops to the
    // login screen instead of retrying forever with a dead credential.
    if (res.status === 401) setToken(null)
    throw new ApiError(
      res.status,
      payload?.error ?? 'http_error',
      payload?.detail ?? payload?.error ?? `HTTP ${res.status}`,
    )
  }

  return payload as T
}

// --- typed helpers ----------------------------------------------------------

export type User = {
  id: number
  username: string
  display_name: string | null
  name: string
  email: string | null
  is_admin: boolean
  unit_pref: 'kg' | 'lb'
  /** Distance display/entry preference. Separate from unit_pref on purpose —
   *  see lib/units. */
  distance_pref: DistancePref
  timezone: string
  share_activity: boolean
  default_rest_seconds: number
}

export async function login(
  username: string,
  password: string,
  device?: string,
): Promise<User> {
  const res = await api<{ token: string; user: User }>('/auth/login', {
    body: { username, password, device },
  })
  setToken(res.token)
  return res.user
}

export async function logout(): Promise<void> {
  try {
    await api('/auth/logout', { method: 'POST', body: {} })
  } finally {
    // Drop the local token even if the revoke call failed — the user asked to
    // sign out, and a network error shouldn't leave them signed in.
    setToken(null)
  }
}

export async function fetchMe(): Promise<User> {
  const res = await api<{ user: User }>('/auth/me')
  return res.user
}

export type Exercise = {
  id: number
  owner_id: number | null
  name: string
  slug: string
  category: string
  primary_muscle: string | null
  equipment: string
  is_unilateral: boolean
  is_bodyweight: boolean
  increment_kg: number
}

export async function fetchExercises(): Promise<Exercise[]> {
  const res = await api<{ exercises: Exercise[] }>('/exercises')
  return res.exercises
}
